import { AuthApi } from '@quadminds/client-apis/auth';
import { Spinner, Text } from '@quadminds/ui-kit';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { BsCheckAll } from 'react-icons/bs';
import { useSearchParams } from 'react-router-dom';
import alert_fill_icon from '../assets/alert_fill.svg';
import check_circle_icon from '../assets/check_circle.svg';
import SignupLayout from '../layout/SignupLayout';
import { isFleetcare, isMindy, isOms } from '../utils';

export const VerifyChangedEmail: React.FC = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const authService = useMemo(() => new AuthApi(), []);
    const [errorEmail, setErrorEmail] = useState<string | null>(null);

    const [verificationStatus, setVerificationStatus] = useState<'pending' | 'success' | 'failed'>('pending');

    useEffect(() => {
        const token = searchParams.get('token');
        const validateToken = async () => {
            try {
                if (token) {
                    const response = await authService.verifyChangedEmail(token);
                    // Verifica la respuesta del servicio
                    if (response.success) {
                        setVerificationStatus('success');
                    } else {
                        setVerificationStatus('failed');
                        setErrorEmail(t('changeVerifyEmail.error.textDefault'));
                    }
                } else {
                    // Token es undefined
                    setVerificationStatus('failed');
                    setErrorEmail(t('changeVerifyEmail.error.textDefault'));
                }
            } catch (error: any) {
                console.error(error);
                if (error.response.data.message === 'The email address is already in use by another account.') {
                    setErrorEmail(t('changeVerifyEmail.error.textDobleEmail'));
                }
                setVerificationStatus('failed');
                setErrorEmail(t('changeVerifyEmail.error.textDefault'));
            }
        };

        // Realiza la verificación al montar el componente
        validateToken();
    }, [authService, searchParams, t]);

    if (isMindy || isFleetcare || isOms) {
        return (
            <>
                {verificationStatus === 'pending' && (
                    <div className="w-full h-screen flex flex-col justify-center content-center items-center">
                        <Spinner size={100} color="primary" />
                    </div>
                )}
                {verificationStatus === 'success' && (
                    <SignupLayout>
                        <div className="w-full">
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4 }}
                                className="space-y-5"
                            >
                                <BsCheckAll className="text-primary-400 text-5xl" />
                                <div className="border-t border-gray-700" />
                                <div>
                                    <h2 className="text-2xl font-poppins font-semibold text-white mb-2">
                                        {t('changeVerifyEmail.success.title')}
                                    </h2>
                                    <p className="text-sm text-gray-400 font-roboto">
                                        {t('changeVerifyEmail.success.text')}
                                    </p>
                                </div>
                            </motion.div>
                        </div>
                    </SignupLayout>
                )}
                {verificationStatus === 'failed' && (
                    <SignupLayout>
                        <div className="w-full">
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4 }}
                                className="space-y-5"
                            >
                                <h2 className="text-2xl font-poppins font-semibold text-white">
                                    {t('changeVerifyEmail.error.title')}
                                </h2>
                                <div className="border-t border-gray-700" />
                                <div className="border border-red-500 rounded-lg p-4 space-y-1">
                                    <p className="text-sm font-roboto text-red-400">{errorEmail}</p>
                                </div>
                            </motion.div>
                        </div>
                    </SignupLayout>
                )}
            </>
        );
    }

    return (
        <>
            {verificationStatus === 'pending' && (
                <div className="w-full h-screen flex flex-col justify-center content-center items-center">
                    <Spinner size={100} color="variant" />
                </div>
            )}
            {verificationStatus === 'success' && (
                <SignupLayout>
                    <div className="flex h-screen">
                        <div className="m-auto text-center p-20">
                            <img src={check_circle_icon} alt="" className="mx-auto my-5" />
                            <Text className="text-4xl mb-5 font-black">{t('changeVerifyEmail.success.title')}</Text>
                            <Text className="text-2xl mb-10 font-normal">{t('changeVerifyEmail.success.text')}</Text>
                        </div>
                    </div>
                </SignupLayout>
            )}
            {verificationStatus === 'failed' && (
                <SignupLayout>
                    <div className="flex h-screen">
                        <div className="m-auto text-center p-20">
                            <img src={alert_fill_icon} alt="" className="mx-auto my-7" />
                            <Text className="text-4xl mb-5 font-black">{t('changeVerifyEmail.error.title')}</Text>
                            <Text className="text-2xl mb-10 font-normal">{errorEmail}</Text>
                        </div>
                    </div>
                </SignupLayout>
            )}
        </>
    );
};

export default VerifyChangedEmail;
